import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Mail, Linkedin, GraduationCap, Loader2, FileText, Briefcase } from 'lucide-react';
import { labApi } from '../services/api';
import ScrollReveal from './ui/ScrollReveal';
import Skeleton from './ui/Skeleton';

const LabMembers = () => {
  const [labMembers, setLabMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);
  const [visibleAlumniCount, setVisibleAlumniCount] = useState(12);

  useEffect(() => {
    labApi.getLabMembers()
      .then(data => setLabMembers(data))
      .catch(err => console.error('Failed to fetch lab members:', err))
      .finally(() => setLoading(false));
  }, []);

  const currentMembers = labMembers.filter(m => !m.is_alumni);
  const alumni = labMembers.filter(m => m.is_alumni);

  return (
    <section id="team" className="py-28 bg-white" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gradient-section mb-4 tracking-[-0.02em]" data-testid="team-heading">
              Lab Members
            </h2>
            <div className="heading-accent-bar"></div>
            <p className="text-lg text-slate-500 max-w-3xl mx-auto font-light tracking-wide">
              Meet our dedicated team of researchers, scientists, and graduate students
              driving innovation in multiscale materials science.
            </p>
          </div>
        </ScrollReveal>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-96 w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <>
            {/* Current Members Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {currentMembers.slice(0, visibleCount).map((member, index) => (
                <ScrollReveal key={member.id} delay={(index % 3) * 150}>
                  <Card 
                    data-testid={`member-card-${member.id}`}
                    className="group overflow-hidden border-gray-200 hover-card-glow h-full flex flex-col"
                  >
                    <div className="relative h-80 overflow-hidden bg-white">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>

                    <CardContent className="p-6 flex-grow flex flex-col">
                      <h3 className="text-xl font-bold text-slate-800 mb-1">
                        {member.name}
                      </h3>
                      <p className="text-sm font-medium text-teal-600 mb-4">
                        {member.title}
                      </p>
                      <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                        {member.bio}
                      </p>
                      
                      <div className="mb-4 mt-auto">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Research Interests:</p>
                        <div className="flex flex-wrap gap-1">
                          {member.research.map((topic, index) => (
                            <span 
                              key={index} 
                              className="text-xs bg-white text-slate-700 px-2 py-1 rounded"
                            >
                              {topic}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                        {member.cv_url && (
                          <button
                            onClick={() => {
                              if (member.cv_url.startsWith('data:')) {
                                // Modern browsers block opening data: URIs directly.
                                // Convert to Blob and open via Object URL.
                                const arr = member.cv_url.split(',');
                                const mime = arr[0].match(/:(.*?);/)[1];
                                const bstr = atob(arr[1]);
                                let n = bstr.length;
                                const u8arr = new Uint8Array(n);
                                while (n--) {
                                  u8arr[n] = bstr.charCodeAt(n);
                                }
                                const blob = new Blob([u8arr], { type: mime });
                                const blobUrl = URL.createObjectURL(blob);
                                window.open(blobUrl, '_blank');
                                // Revoke later if possible, but for a new tab it's usually fine
                              } else {
                                window.open(member.cv_url, '_blank');
                              }
                            }}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800 transition-colors"
                            data-testid={`member-cv-${member.id}`}
                          >
                            <FileText size={18} />
                            View CV
                          </button>
                        )}
                        <a
                          href={`mailto:${member.email}`}
                          className="text-sm text-slate-600 hover:text-teal-600 transition-colors duration-200 truncate"
                          data-testid={`member-email-${member.id}`}
                        >
                          {member.email}
                        </a>
                        <a
                          href={member.linkedin}
                          className="text-slate-600 hover:text-teal-600 transition-colors duration-200"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="LinkedIn"
                        >
                          <Linkedin size={18} />
                        </a>
                        <a
                          href={member.scholar}
                          className="text-slate-600 hover:text-teal-600 transition-colors duration-200"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Google Scholar"
                        >
                          <GraduationCap size={18} />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                </ScrollReveal>
              ))}
            </div>
            
            <div className="flex flex-col items-center gap-4 mb-16">
              {visibleCount < currentMembers.length && (
                <button 
                  onClick={() => setVisibleCount(prev => prev + 6)}
                  className="px-8 py-3 bg-white border-2 border-teal-600 text-teal-600 font-semibold rounded-full hover:bg-teal-50 transition-colors duration-300 shadow-sm"
                >
                  Load More Members
                </button>
              )}
              {visibleCount > 6 && (
                <button 
                  onClick={() => {
                    setVisibleCount(6);
                    document.getElementById('team')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors duration-300 flex items-center gap-1"
                >
                  Show Less ⬆
                </button>
              )}
            </div>

            {/* Alumni Section */}
            {alumni.length > 0 && (
              <div className="mt-20 pt-12 border-t border-gray-200">
                <ScrollReveal>
                  <div className="text-center mb-12">
                    <h3 className="text-3xl font-bold text-slate-800 mb-4">Our Alumni</h3>
                    <div className="w-16 h-1 bg-slate-300 mx-auto"></div>
                  </div>
                </ScrollReveal>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {alumni.slice(0, visibleAlumniCount).map((member, index) => (
                    <ScrollReveal key={member.id} delay={(index % 4) * 100}>
                      <div 
                        className="bg-slate-50 hover:bg-white border border-slate-200 rounded-lg p-4 flex flex-col transition-colors duration-200 h-full"
                      >
                        <h4 className="font-semibold text-slate-800 flex items-center mb-1">
                          <GraduationCap className="w-4 h-4 mr-2 text-teal-600" />
                          {member.name}
                        </h4>
                        {member.current_workplace && (
                          <p className="text-sm text-slate-600 flex items-center mt-auto pt-2">
                            <Briefcase className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                            {member.current_workplace}
                          </p>
                        )}
                      </div>
                    </ScrollReveal>
                  ))}
                </div>

                <div className="flex flex-col items-center gap-3 mt-8">
                  {visibleAlumniCount < alumni.length && (
                    <button 
                      onClick={() => setVisibleAlumniCount(prev => prev + 12)}
                      className="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors duration-200"
                    >
                      Show More Alumni ⬇
                    </button>
                  )}
                  {visibleAlumniCount > 12 && (
                    <button 
                      onClick={() => setVisibleAlumniCount(12)}
                      className="text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors duration-200"
                    >
                      Show Less ⬆
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default LabMembers;



